import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { lookup } from '../services/api';

function EditLookupModal({ open, onClose, onSuccess, filterOptions, item }) {
  const [formData, setFormData] = useState({
    model: '',
    brand: '',
    wholesale_price: '',
    retail_price: '',
    supplier: '',
    part_category: '',
    lcd_type: '',
    notes: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newOptions, setNewOptions] = useState({
    brand: '',
    part_category: '',
    lcd_type: '',
    supplier: '',
  });
  const [showNewFields, setShowNewFields] = useState({
    brand: false,
    part_category: false,
    lcd_type: false,
    supplier: false,
  });

  // Reset form when modal opens/closes or item changes
  useEffect(() => {
    if (open && item) {
      setFormData({
        model: item.Model || '',
        brand: item.Brand || '',
        wholesale_price: item.WholesalePrice || '',
        retail_price: item.RetailPrice || '',
        supplier: item.Supplier || '',
        part_category: item.PartCategory || '',
        lcd_type: item.LcdType || '',
        notes: item.Notes || '',
      });
      setError(null);
      setShowNewFields({
        brand: false,
        part_category: false,
        lcd_type: false,
        supplier: false,
      });
      setNewOptions({
        brand: '',
        part_category: '',
        lcd_type: '',
        supplier: '',
      });
    }
  }, [open, item]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleNewOptionChange = (field, value) => {
    setNewOptions(prev => ({ ...prev, [field]: value }));
  };

  const handleAddNewOption = (field) => {
    if (newOptions[field].trim()) {
      setFormData(prev => ({ ...prev, [field]: newOptions[field].trim() }));
      setShowNewFields(prev => ({ ...prev, [field]: false }));
      setNewOptions(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.model.trim()) {
      setError('Model is required');
      return;
    }

    if (!formData.supplier.trim()) {
      setError('Supplier is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        Model: formData.model.trim(),
        Supplier: formData.supplier.trim(),
        Brand: formData.brand.trim() || null,
        WholesalePrice: formData.wholesale_price ? parseFloat(formData.wholesale_price) : null,
        RetailPrice: formData.retail_price ? parseFloat(formData.retail_price) : null,
        PartCategory: formData.part_category.trim() || null,
        LcdType: formData.lcd_type.trim() || null,
        Notes: formData.notes.trim() || null,
      };

      const response = await lookup.updateQuaipo(item.Id, payload);
      onSuccess(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.Error || err.message || 'Failed to update lookup entry');
    } finally {
      setLoading(false);
    }
  };

  const renderSelectField = (field, label, options, required = false) => {
    const showNew = showNewFields[field];
    const fieldOptions = options || [];

    return (
      <FormControl fullWidth required={required}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={showNew ? 'ADD_NEW' : formData[field]}
          onChange={(e) => {
            if (e.target.value === 'ADD_NEW') {
              setShowNewFields(prev => ({ ...prev, [field]: true }));
            } else {
              handleInputChange(field, e.target.value);
            }
          }}
          label={label}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {fieldOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
          <MenuItem value="ADD_NEW">
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
              <AddIcon sx={{ mr: 1, fontSize: 16 }} />
              Add New {label}
            </Box>
          </MenuItem>
        </Select>
        {showNew && (
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder={`Enter new ${label.toLowerCase()}`}
              value={newOptions[field]}
              onChange={(e) => handleNewOptionChange(field, e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddNewOption(field);
                }
              }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => handleAddNewOption(field)}
              disabled={!newOptions[field].trim()}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              }}
            >
              Add
            </Button>
            <Button
              size="small"
              onClick={() => setShowNewFields(prev => ({ ...prev, [field]: false }))}
              sx={{
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </FormControl>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Edit Quiapo Parts Lookup
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Required Fields */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Required Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Product Model"
              value={formData.model}
              onChange={(e) => handleInputChange('model', e.target.value)}
              placeholder="e.g., iPhone 13 Pro Max, A31S, etc."
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Supplier"
              value={formData.supplier}
              onChange={(e) => handleInputChange('supplier', e.target.value)}
              placeholder="e.g., ASHTEL, OTHER SUPPLIER"
            />
          </Grid>

          {/* Product Details */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Product Details
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            {renderSelectField('brand', 'Brand', filterOptions?.brands)}
          </Grid>

          <Grid item xs={12} md={6}>
            {renderSelectField('part_category', 'Part Category', filterOptions?.part_categories)}
          </Grid>

          <Grid item xs={12} md={6}>
            {renderSelectField('lcd_type', 'LCD Type', filterOptions?.lcd_types)}
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional information..."
              multiline
              rows={2}
            />
          </Grid>

          {/* Pricing */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
              Pricing Information
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Wholesale Price"
              type="number"
              value={formData.wholesale_price}
              onChange={(e) => handleInputChange('wholesale_price', e.target.value)}
              placeholder="0.00"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>₱</Typography>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Retail Price"
              type="number"
              value={formData.retail_price}
              onChange={(e) => handleInputChange('retail_price', e.target.value)}
              placeholder="0.00"
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>₱</Typography>,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.model.trim() || !formData.supplier.trim()}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark'
            },
            '&:disabled': {
              backgroundColor: 'action.disabledBackground',
              color: 'action.disabled'
            }
          }}
        >
          {loading ? 'Updating...' : 'Update Lookup Entry'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditLookupModal;